import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import { Box, Typography } from '@mui/material';
import { ChartBarComparisonExtendedProps } from './types';
import { convertUnits } from './Chart.Common';

const ChartBarComparison: React.FC<ChartBarComparisonExtendedProps> = ({
                                                                           data,
                                                                           selectedProducts,
                                                                           setSelectedProducts,
                                                                           compoundFilter,
                                                                           setCompoundFilter,
                                                                           showNonNumeric,
                                                                           setShowNonNumeric,
                                                                           selectedClass,
                                                                           setSelectedClass,
                                                                           selectedUnit,
                                                                           setSelectedUnit,
                                                                           selectedDenominator,
                                                                           setSelectedDenominator,
                                                                           selectedDiseaseCategory,
                                                                           setSelectedDiseaseCategory,
                                                                           diseaseInchiKeys,
                                                                           setDiseaseInchiKeys,
                                                                           selectedCompounds
                                                                       }) => {
    const filteredItems = useMemo(
        () => data.filter((item: any) => item.HEADER === 'List of Products Analysis Values'),
        [data]
    );

    // Convert units
    const convertedItems = useMemo(() =>
            convertUnits(filteredItems, selectedUnit, selectedDenominator),
        [filteredItems, selectedUnit, selectedDenominator]
    );

    // Filter items by class
    const classFilteredItems = useMemo(() => {
        if (selectedClass === 'All') return convertedItems;
        return convertedItems
            .map((item: any) => ({
                ...item,
                DATA: item.DATA.filter((row: any) => row.CLASS === selectedClass),
            }))
            .filter((item: any) => item.DATA.length > 0);
    }, [convertedItems, selectedClass]);

    // Compute chart data with multiple datasets
    const chartData = useMemo(() => {
        console.log('Selected Products:', selectedProducts);
        console.log('Class Filtered Items:', classFilteredItems);
        console.log('Compound Filter:', compoundFilter);
        console.log('Show Non-Numeric:', showNonNumeric);
        console.log('Selected Unit:', selectedUnit);
        console.log('Selected Denominator:', selectedDenominator);
        console.log('Selected Disease Category:', selectedDiseaseCategory);
        console.log('Disease INCHIKEYs:', diseaseInchiKeys);
        console.log('Selected Compounds:', selectedCompounds);

        if (selectedProducts.length < 2 || selectedProducts.length > 2) {
            console.log('Less or more than two products selected');
            return { labels: [], datasets: [] };
        }

        const leftItems = classFilteredItems.flatMap(item =>
            item.DATA?.filter((d: { PRODUCT?: string; ANALYSIS_TYPE?: string }) =>
                `${d.PRODUCT || 'Unknown'} (${d.ANALYSIS_TYPE || 'Unknown'})` === selectedProducts[0]
            ) || []
        );

        const rightItems = classFilteredItems.flatMap(item =>
            item.DATA?.filter((d: { PRODUCT?: string; ANALYSIS_TYPE?: string }) =>
                `${d.PRODUCT || 'Unknown'} (${d.ANALYSIS_TYPE || 'Unknown'})` === selectedProducts[1]
            ) || []
        );

        console.log('Left Items:', leftItems);
        console.log('Right Items:', rightItems);

        if (!leftItems.length || !rightItems.length) {
            console.log('One or both products not found in filtered items');
            return { labels: [], datasets: [] };
        }

        const allCompounds = new Set<string>();
        const leftCompounds = new Map<string, { average: number, stdev: number | null, replicates: number | null, unit: string, originalValue: any }>();
        const rightCompounds = new Map<string, { average: number, stdev: number | null, replicates: number | null, unit: string, originalValue: any }>();

        leftItems.forEach((row: any) => {
            const inchiKey = row.INCHIKEY || '';
            if (diseaseInchiKeys.length > 0 && !diseaseInchiKeys.includes(inchiKey)) {
                return;
            }
            const rawValue = row.AVERAGE;
            const numericValue = Number(rawValue);
            const isNumericAverage = !isNaN(numericValue) && rawValue !== null;
            const lodValue = Number(row.LOD);
            const loqValue = Number(row.LOQ);
            const hasValidLod = !isNaN(lodValue);
            const hasValidLoq = !isNaN(loqValue);
            let value: number | null = null;

            if (isNumericAverage) {
                value = numericValue;
            } else if (showNonNumeric && typeof rawValue === 'string') {
                if (rawValue.includes('D') && hasValidLod) {
                    value = lodValue;
                } else if (rawValue.includes('Q') && hasValidLoq) {
                    value = loqValue;
                }
            }

            if (value !== null && value > 0 && (isNumericAverage || showNonNumeric)) {
                allCompounds.add(row.COMPOUND_NAME);
                leftCompounds.set(row.COMPOUND_NAME, {
                    average: value,
                    stdev: Number(row.STD_DEV) || null,
                    replicates: Number(row.NUMBER_OF_REPLICATES) || null,
                    unit: row.UNIT || `${selectedUnit}/${selectedDenominator}`,
                    originalValue: rawValue ?? 'N/A'
                });
            }
        });

        rightItems.forEach((row: any) => {
            const inchiKey = row.INCHIKEY || '';
            if (diseaseInchiKeys.length > 0 && !diseaseInchiKeys.includes(inchiKey)) {
                return;
            }
            const rawValue = row.AVERAGE;
            const numericValue = Number(rawValue);
            const isNumericAverage = !isNaN(numericValue) && rawValue !== null;
            const lodValue = Number(row.LOD);
            const loqValue = Number(row.LOQ);
            const hasValidLod = !isNaN(lodValue);
            const hasValidLoq = !isNaN(loqValue);
            let value: number | null = null;

            if (isNumericAverage) {
                value = numericValue;
            } else if (showNonNumeric && typeof rawValue === 'string') {
                if (rawValue.includes('D') && hasValidLod) {
                    value = lodValue;
                } else if (rawValue.includes('Q') && hasValidLoq) {
                    value = loqValue;
                }
            }

            if (value !== null && value > 0 && (isNumericAverage || showNonNumeric)) {
                allCompounds.add(row.COMPOUND_NAME);
                rightCompounds.set(row.COMPOUND_NAME, {
                    average: value,
                    stdev: Number(row.STD_DEV) || null,
                    replicates: Number(row.NUMBER_OF_REPLICATES) || null,
                    unit: row.UNIT || `${selectedUnit}/${selectedDenominator}`,
                    originalValue: rawValue ?? 'N/A'
                });
            }
        });

        let validCompounds = Array.from(allCompounds).filter(compound =>
            (leftCompounds.get(compound)?.average || 0) > 0 || (rightCompounds.get(compound)?.average || 0) > 0
        );

        // Apply compound filter
        if (selectedCompounds.length > 0 && !selectedCompounds.includes('All')) {
            validCompounds = validCompounds.filter(compound => selectedCompounds.includes(compound));
        }

        const labels = compoundFilter === 'inCommon'
            ? validCompounds.filter(compound =>
                (leftCompounds.get(compound)?.average || 0) > 0 && (rightCompounds.get(compound)?.average || 0) > 0
            )
            : compoundFilter === 'notInCommon'
                ? validCompounds.filter(compound =>
                    !((leftCompounds.get(compound)?.average || 0) > 0 && (rightCompounds.get(compound)?.average || 0) > 0)
                )
                : validCompounds;

        const leftValues: number[] = [];
        const rightValues: number[] = [];
        const leftExtras: { average: number, stdev: number | null, replicates: number | null, unit: string, originalValue: any }[] = [];
        const rightExtras: { average: number, stdev: number | null, replicates: number | null, unit: string, originalValue: any }[] = [];

        labels.forEach(compound => {
            const leftData = leftCompounds.get(compound) || { average: 0, stdev: null, replicates: null, unit: `${selectedUnit}/${selectedDenominator}`, originalValue: 'N/A' };
            const rightData = rightCompounds.get(compound) || { average: 0, stdev: null, replicates: null, unit: `${selectedUnit}/${selectedDenominator}`, originalValue: 'N/A' };
            const leftValue = leftData.average;
            const rightValue = rightData.average;
            const total = leftValue + rightValue;

            if (total > 0) {
                leftValues.push((leftValue / total) * 100);
                rightValues.push((rightValue / total) * 100);
            } else {
                leftValues.push(0);
                rightValues.push(0);
            }
            leftExtras.push(leftData);
            rightExtras.push(rightData);
        });

        const result = {
            labels,
            datasets: [
                {
                    label: selectedProducts[0] || 'Left',
                    data: leftValues,
                    backgroundColor: 'rgba(54, 162, 235, 1)', // Darker blue
                    extras: leftExtras,
                },
                {
                    label: selectedProducts[1] || 'Right',
                    data: rightValues,
                    backgroundColor: 'rgba(255, 99, 132, 1)', // Darker red
                    extras: rightExtras,
                },
            ],
        };

        console.log('Final Chart Data:', result);
        return result;
    }, [classFilteredItems, selectedProducts, compoundFilter, showNonNumeric, selectedUnit, selectedDenominator, diseaseInchiKeys, selectedCompounds]);

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top' as const,
            },
            title: {
                display: true,
                text: `Comparison of ${selectedProducts[0] || 'Left'} vs ${selectedProducts[1] || 'Right'} (${selectedUnit}/${selectedDenominator})`,
            },
            tooltip: {
                callbacks: {
                    label: (context: any) => {
                        const dataset = context.dataset;
                        const value = context.parsed.y;
                        const index = context.dataIndex;
                        const extra = dataset.extras[index];
                        return [
                            `${dataset.label}: ${value.toFixed(2)}%`,
                            `Amount: ${typeof extra.originalValue === 'number' ? extra.originalValue.toFixed(2) : extra.originalValue}`,
                            `Std Dev: ${extra.stdev !== null ? extra.stdev.toFixed(2) : 'N/A'}`,
                            `Replicates: ${extra.replicates !== null ? extra.replicates : 'N/A'}`,
                        ];
                    },
                },
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Compounds',
                },
                stacked: true,
            },
            y: {
                title: {
                    display: true,
                    text: 'Percentage (%)',
                },
                stacked: true,
                beginAtZero: true,
                max: 100,
            },
        },
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                {chartData.labels.length > 0 ? (
                    <Box sx={{ width: '80%' }}>
                        <Bar data={chartData} options={options} />
                    </Box>
                ) : (
                    <Typography sx={{fontSize: 'large' }}>Select two products to compare.</Typography>
                )}
            </Box>
        </Box>
    );
};

export default ChartBarComparison;
